import { getPaletteBySwatch } from "@/lib/paletteBySwatch";
import AmenityBubble from "./AmenityBubble";
import { layoutBookingTile } from "./BrickWeekSchedule.utils";
import Bubble from "./Bubble";
import Buffers from "./Buffers";
import { hasAnyBuffer } from "@/utils/bufferFlags";
import type { Amenity } from "@/types/Amenity";
import type { AmenityBooking } from "@/types/AmenityBooking";
import type { Booking } from "@/types/Booking";
import type { Member } from "@/types/Member";
import type { Room } from "@/types/Room";

interface BookingColumnProps {
  bookings: Booking[];
  item: Room | Amenity;
  itemType: "room" | "amenity";
  date: Date;
  segments: string[];
  hiddenEarly: boolean;
  timesToHideEarly: string[];
  keyHeight: number;
  onTimeSlotClick: (event: React.MouseEvent, time: string) => void;
  member: Member | null;
  locationSlug?: string;
}

export const BookingColumn = ({
  bookings,
  item,
  itemType,
  segments,
  hiddenEarly,
  timesToHideEarly,
  keyHeight,
  onTimeSlotClick,
  member,
  locationSlug,
}: BookingColumnProps) => {
  const visibleSegments = hiddenEarly ? segments.filter((t) => !timesToHideEarly.includes(t)) : segments;

  // Get background color based on item type
  const _bg =
    itemType === "room" && "swatch" in item
      ? getPaletteBySwatch(item.swatch)?.transparent || "bg-gray-100/10"
      : "bg-purple-100/20";

  return (
    <div className={`flex-1 ${_bg} h-full rounded overflow-hidden relative pointer-events-none`} key={item.id}>
      {/* Render clickable time segments */}
      <div className="absolute inset-0 flex flex-col">
        {visibleSegments.map((time, segIdx) => (
          <div
            key={`${item.id}-${time}-${segIdx}`}
            data-time={time}
            data-item-id={item.id}
            className="border-b border-gray-200 border-dotted hover:bg-amber-100 hover:cursor-pointer pointer-events-auto"
            style={{
              height: `${keyHeight}px`,
              minHeight: `${keyHeight}px`,
              maxHeight: `${keyHeight}px`,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onTimeSlotClick(e, time);
            }}
          />
        ))}
      </div>

      {/* Render bookings */}
      {bookings.map((booking) => {
        // Shared placement: a midnight end ("0000") reads as 24:00 and the grid runs to 24:00.
        const tile = layoutBookingTile(booking.startTime, booking.endTime, visibleSegments, hiddenEarly, keyHeight);
        if (!tile) return null;
        const { top: _top, height: _height, isClippedAtStart, isClippedAtEnd } = tile;
        const half = parseInt((keyHeight / 2).toString(), 10);

        return (
          <div key={booking.id || booking.startTime}>
            {hasAnyBuffer(booking) && !isClippedAtStart && !isClippedAtEnd && (
              <Buffers booking={booking} top={_top} height={_height} half={half} member={member} />
            )}
            <Bubble
              booking={booking}
              member={member}
              top={_top}
              height={_height}
              allowClick
              locationSlug={locationSlug}
            />
          </div>
        );
      })}
    </div>
  );
};

interface AmenityBookingColumnProps {
  amenityBookings: AmenityBooking[];
  amenity: Amenity;
  date: Date;
  segments: string[];
  hiddenEarly: boolean;
  timesToHideEarly: string[];
  keyHeight: number;
  member: Member | null;
}

export const AmenityBookingColumn = ({
  amenityBookings,
  amenity,
  segments,
  hiddenEarly,
  timesToHideEarly,
  keyHeight,
  member,
}: AmenityBookingColumnProps) => {
  const visibleSegments = hiddenEarly ? segments.filter((t) => !timesToHideEarly.includes(t)) : segments;
  const _bg = "bg-gray-400/20";

  // Assignee display name per amenity booking (server-enriched) so the tile can show WHO booked it.
  const ownerNameById = new Map(amenityBookings.map((ab) => [ab.id, ab.memberName]));

  // Convert AmenityBookings to a Booking-like structure for rendering. R23 — `hasBuffers`
  // mirrors the parent room booking's flag so the amenity tile gets the same pre/post
  // buffer stripes. See hearth/docs/BOOKING_RULES.md#r23.
  const renderableBookings: Booking[] = amenityBookings.map((ab) => ({
    id: ab.id,
    memberId: ab.memberId || "",
    locationId: ab.locationId || "",
    roomId: ab.roomId || "",
    date: ab.date || new Date(),
    startTime: ab.startTime ? ab.startTime.padStart(4, "0") : "0000",
    endTime: ab.endTime ? ab.endTime.padStart(4, "0") : "0000",
    type: "prospect",
    description: ab.description || amenity.name,
    // A missing flag defaults to `true` to match how the backend treats `hasBuffers`
    // everywhere (`?? true`); legacy/imported rows without the field still mirror a
    // parent that has buffers, rather than silently dropping the stripes. The directional
    // flags are carried through too so a single-sided parent buffer renders correctly on
    // the amenity tile (R23) rather than collapsing to all-or-nothing.
    hasBuffers: ab.hasBuffers ?? true,
    hasPreBuffer: ab.hasPreBuffer,
    hasPostBuffer: ab.hasPostBuffer,
    createdAt: ab.createdAt ?? "",
    updatedAt: ab.updatedAt ?? "",
    wasWarnedAboutUnbookableGap: false,
    isGroupBooking: false,
    gratis: false,
    canOverride: false,
  }));

  return (
    <div className={`flex-1 ${_bg} h-full rounded overflow-hidden relative pointer-events-none`} key={amenity.id}>
      {/* Render non-interactive time segments */}
      <div className="absolute inset-0 flex flex-col">
        {visibleSegments.map((time, segIdx) => (
          <div
            key={`${amenity.id}-${time}-${segIdx}`}
            data-time={time}
            data-amenity-id={amenity.id}
            className="border-b border-gray-200 dark:border-slate-800 border-dotted"
            style={{
              height: `${keyHeight}px`,
              minHeight: `${keyHeight}px`,
              maxHeight: `${keyHeight}px`,
            }}
          />
        ))}
      </div>

      {/* Render amenity bookings */}
      {renderableBookings.map((booking) => {
        // Shared placement: a midnight end ("0000") reads as 24:00 and the grid runs to 24:00.
        const tile = layoutBookingTile(booking.startTime, booking.endTime, visibleSegments, hiddenEarly, keyHeight);
        if (!tile) return null;
        // Buffers don't render when the tile is clipped at either edge (matches room bookings).
        const { top: _top, height: _height, isClippedAtStart: isAmenityClippedAtStart, isClippedAtEnd: isAmenityClippedAtEnd } = tile;
        const half = parseInt((keyHeight / 2).toString(), 10);

        return (
          <div key={booking.id || booking.startTime}>
            {/* R23 — amenity tiles render the same pre/post buffer stripes as their parent */}
            {hasAnyBuffer(booking) && !isAmenityClippedAtStart && !isAmenityClippedAtEnd && (
              <Buffers booking={booking} top={_top} height={_height} half={half} member={member} variant="amenity" />
            )}
            <AmenityBubble
              booking={booking}
              member={member}
              ownerName={ownerNameById.get(booking.id) ?? undefined}
              top={_top}
              height={_height}
            />
          </div>
        );
      })}
    </div>
  );
};
